/**
 * Capitalizes the first character of a string.
 * @param str - The string to capitalize.
 * @returns The capitalized string.
 */
export const capitalizeFirst = (str: string = ""): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Naive depluralizer: removes common plural suffixes from English words.
 * @param str - The plural word to singularize.
 * @returns The singularized form of the word.
 */
export const depluralize = (str: string = ""): string => {
  if (!str) return "";
  if (str.endsWith("ies")) return str.slice(0, -3) + "y";
  if (str.endsWith("sses") || str.endsWith("shes") || str.endsWith("ches")) return str.slice(0, -2); // handles e.g., 'watches' → 'watch'
  if (str.endsWith("s") && !str.endsWith("ss")) return str.slice(0, -1);
  return str;
};

/**
 * Domain acronyms that must render in ALL CAPS even when they appear as a
 * lowercase segment of a snake_case/kebab-case field name. Matching is
 * case-insensitive on the whole word.
 */
const TITLE_CASE_ACRONYMS = new Set([
  "ytd",
  "id",
  "icd",
  "cpt",
  "ndc",
  "dob",
  "ssn",
  "npi",
  "tin",
  "eob",
  "eor",
  "ein",
  "hra",
  "hsa",
  "pos",
  "fsa",
]);

/**
 * Converts snake_case, kebab-case, or spaced text into title case (capitalized words).
 * Recognized domain acronyms (YTD, ID, ICD, CPT, NDC, DOB, SSN, NPI, TIN, EOB,
 * EOR, EIN, HRA, HSA, POS, FSA) render in ALL CAPS so labels like
 * "ytd_total_paid" render as "YTD Total Paid" instead of "Ytd Total Paid".
 * @param value - The string to format.
 * @returns The formatted string.
 */
export const formatToTitleCase = (value?: string): string => {
  if (!value) return "";

  return value
    .replace(/[_-]+/g, " ") // replace underscores or hyphens with spaces
    .trim()
    .split(/\s+/) // split by one or more spaces
    .map((word) => {
      if (TITLE_CASE_ACRONYMS.has(word.toLowerCase())) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

/** Formats a claimant relationship value for display. Maps "self" → "Subscriber". */
export const formatRelationship = (value?: string): string => {
  if (!value) return "";
  if (value === "self") return "Subscriber";
  return formatToTitleCase(value);
};

export const convertCamelCaseToTitleCase = (text?: string) => {
  if (!text || text.length === 0) {
    return "";
  }
  const textWithSpaces = text.replace(/([A-Z])/g, " $1");
  const titleCaseText = textWithSpaces[0].toUpperCase() + textWithSpaces.slice(1);
  return titleCaseText;
};
